import React, { useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import { ErrorMessage, Icon, Stack, Text, TextField } from '@ui'
import { useBalance } from 'hooks/useBalance'
import { usePanelActions } from 'routes/layouts/hooks/usePanelActions'
import { shortAddress } from 'ui/utils/utils'
import { Address } from 'use-towns-client'
import { isAddress } from 'viem'

import { SingleSelectionSelector } from './SingleSelectionSelector'
import { TransferSchema } from './transferAssetsSchema'
import { WalletWithBalance } from './WalletWithBalance'

const LINK_WALLET = 'Link Wallet' as const

export function WalletSelector(props: {
    aaAddress?: Address
    fromWallet?: Address
    linkedWallets: string[] | undefined
    showBalance?: boolean
}) {
    const { aaAddress, fromWallet, linkedWallets, showBalance = false } = props
    const { watch, setValue, formState, register } = useFormContext<TransferSchema>()
    const recipient = watch('recipient')

    const _wallets: { address: Address | typeof LINK_WALLET }[] = useMemo(() => {
        return (
            linkedWallets
                ?.filter((w) => w.toLowerCase() !== fromWallet?.toLowerCase())
                .map((w) => ({ address: w as Address })) ?? []
        )
    }, [fromWallet, linkedWallets])

    const label = _wallets.length === 0 ? 'No linked wallets' : 'Linked Wallets'
    const options = _wallets.concat([{ address: LINK_WALLET }])

    const onAddItem = (address: Address | typeof LINK_WALLET) => {
        if (address === LINK_WALLET) {
            return
        }
        setValue('recipient', address, { shouldValidate: true })
    }

    const singleSelection = (removeCb: () => void) => {
        const onRemoveClick = () => {
            setValue('recipient', undefined, { shouldValidate: true })
            removeCb()
        }

        if (!recipient) {
            return
        }
        const isAAWallet = aaAddress?.toLowerCase() === recipient.toLowerCase()

        return (
            <Stack padding background="level2" rounded="sm" data-testid="selected-wallet">
                <WalletWithBalance
                    address={recipient}
                    isAbstractAccount={isAAWallet}
                    showBalance={showBalance}
                    onRemoveClick={onRemoveClick}
                />
            </Stack>
        )
    }

    return (
        <Stack gap data-testid="linked-wallet-search">
            {/* register a hidden field to trigger validation */}
            <TextField style={{ display: 'none' }} {...register('recipient')} />
            <SingleSelectionSelector
                options={options}
                label={label}
                placeholder="Choose linked wallet or enter Base address (0x)"
                keys={['address']}
                optionRenderer={(args) => (
                    <WalletsOption
                        address={args.option.address}
                        aaAddress={aaAddress}
                        showBalance={showBalance}
                        onAddItem={() => onAddItem(args.option.address)}
                    />
                )}
                emptySelectionElement={(props) => (
                    <EmptyWalletOption
                        searchTerm={props.searchTerm}
                        onAddItem={(address) => onAddItem(address)}
                    />
                )}
                getOptionKey={(opt) => opt.address}
                singleSelection={(onRemoveClick) => singleSelection(onRemoveClick)}
            />
            <ErrorMessage preventSpace errors={formState.errors} fieldName="recipient" />
        </Stack>
    )
}

function EmptyWalletOption(props: {
    searchTerm: string
    onAddItem: (searchItem: Address) => void
}) {
    const { openPanel } = usePanelActions()

    const onLinkWallet = () => {
        openPanel('wallets')
    }
    return (
        <Stack padding gap rounded="sm" background="level2" boxShadow="card">
            {isAddress(props.searchTerm) ? (
                <WalletsOption
                    address={props.searchTerm}
                    onAddItem={() => props.onAddItem(props.searchTerm as Address)}
                />
            ) : (
                <Stack horizontal alignItems="center" gap="sm" color="gray2">
                    <Icon type="alert" size="square_sm" />
                    <Text>Enter a valid Base address</Text>
                </Stack>
            )}
            <Stack
                horizontal
                paddingY="xs"
                cursor="pointer"
                justifyContent="spaceBetween"
                onClick={onLinkWallet}
            >
                <Text>{LINK_WALLET}</Text>
            </Stack>
        </Stack>
    )
}

function WalletsOption(props: {
    address: string
    onAddItem: (customKey?: string) => void
    aaAddress?: Address
    showBalance?: boolean
}) {
    const { address, onAddItem, aaAddress, showBalance = true } = props
    const { openPanel } = usePanelActions()
    const balance = useBalance({
        address: address as Address,
        enabled: address !== LINK_WALLET && showBalance,
    })
    const onClick = () => {
        if (address !== LINK_WALLET) {
            onAddItem(address)
        } else {
            openPanel('wallets')
        }
    }

    const isAAWallet = aaAddress?.toLowerCase() === address.toLowerCase()

    return (
        <Stack
            horizontal
            hoverable
            paddingBottom="sm"
            rounded="sm"
            cursor="pointer"
            justifyContent="spaceBetween"
            data-testid={`linked-wallet-option-${address}`}
            onClick={onClick}
        >
            <Stack gap="sm">
                {isAAWallet && <Text>Towns Wallet</Text>}
                <Text color={isAAWallet ? 'gray2' : 'gray1'} size={isAAWallet ? 'xs' : 'md'}>
                    {address === LINK_WALLET ? LINK_WALLET : shortAddress(address)}
                </Text>
            </Stack>
            {address !== LINK_WALLET && showBalance && (
                <Stack horizontal gap="sm">
                    {balance.data?.formatted ?? 0} {balance.data?.symbol ?? ''}
                    <Icon type="base" size="square_sm" color="coinbaseBlue" />
                </Stack>
            )}
        </Stack>
    )
}
